import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { User, LayoutDashboard, Heart, Settings, LogOut, ChevronDown } from 'lucide-react';
import logo from '../assets/logo.png';
import { supabaseManager } from '../utils/supabaseManager';

const Header: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'login' | 'register' | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [userProfile, setUserProfile] = useState({
    username: 'no name',
    email: 'user@example.com',
    avatarUrl: '',
    isLoggedIn: false,
  });

  useEffect(() => {
    const authData = localStorage.getItem('hf_auth_user');
    if (authData) {
      try {
        setUserProfile(JSON.parse(authData));
      } catch (e) {}
    } else {
      const defaultUser = {
        username: 'no name',
        email: 'user@example.com',
        avatarUrl: '',
        isLoggedIn: false,
      };
      localStorage.setItem('hf_auth_user', JSON.stringify(defaultUser));
      setUserProfile(defaultUser);
    }

    const handleAuthChange = () => {
      const data = localStorage.getItem('hf_auth_user');
      if (data) {
        try {
          setUserProfile(JSON.parse(data));
        } catch (e) {}
      } else {
        setUserProfile({
          username: 'no name',
          email: 'user@example.com',
          avatarUrl: '',
          isLoggedIn: false,
        });
      }
    };

    window.addEventListener('hf-auth-change', handleAuthChange);
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('hf-auth-change', handleAuthChange);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    if (!supabaseManager.isInitialized) return;
    await supabaseManager.signOut();
    setShowDropdown(false);
    navigate('/');
  };

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput) {
      setErrorMessage('メールアドレスは必須項目です。');
      return;
    }

    // 新規登録の時のパスワードバリデーション
    if (modalType === 'register') {
      if (passwordInput.length < 6) {
        setErrorMessage('パスワードは6文字以上で入力してください。');
        return;
      }
      const hasUpper = /[A-Z]/.test(passwordInput);
      const hasLower = /[a-z]/.test(passwordInput);
      if (!hasUpper || !hasLower) {
        setErrorMessage(
          'パスワードにはアルファベットの大文字と小文字をそれぞれ1文字以上含めてください。',
        );
        return;
      }
    }

    if (!supabaseManager.isInitialized) {
      setErrorMessage('認証サーバーに接続されていません。');
      return;
    }

    try {
      if (modalType === 'login') {
        const { data, error } = await supabaseManager.signIn(emailInput, passwordInput);
        if (error) {
          setErrorMessage(`ログインに失敗しました: ${error.message}`);
          return;
        }
      } else {
        const { data, error } = await supabaseManager.signUp(emailInput, passwordInput, 'no name');
        if (error) {
          setErrorMessage(`登録に失敗しました: ${error.message}`);
          return;
        }
        if (!data?.session) {
          setErrorMessage('仮登録しました。送付されたメール内のリンクをクリックして登録を完了してください。');
          setEmailInput('');
          setPasswordInput('');
          setTimeout(() => {
            setShowModal(false);
            setErrorMessage('');
          }, 6000);
          return;
        }
      }
    } catch (err: any) {
      setErrorMessage(`認証中にエラーが発生しました: ${err.message}`);
      return;
    }

    setEmailInput('');
    setPasswordInput('');
    setErrorMessage('');
    setShowModal(false);
  };


  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 20px',
    backgroundColor: 'var(--color-surface)',
    borderBottom: '1px solid rgba(231, 228, 223, 0.1)',
    zIndex: 10,
  };

  const logoStyle: React.CSSProperties = {
    height: 'var(--font-size-hero)',
    cursor: 'pointer',
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    color: 'var(--color-text)',
    padding: '8px 16px',
    borderRadius: '12px',
    marginLeft: '10px',
    fontSize: 'var(--font-size-caption)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text)',
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(26, 28, 31, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
  };

  const modalContentStyle: React.CSSProperties = {
    padding: '40px',
    width: '400px',
    maxWidth: '90%',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'center',
  };

  const dropdownMenuStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '10px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    padding: '8px 0',
    minWidth: '200px',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 100,
  };

  const dropdownItemStyle: React.CSSProperties = {
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: 'var(--color-text)',
    textDecoration: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
  };

  const openModal = (type: 'login' | 'register') => {
    setModalType(type);
    setShowModal(true);
  };

  return (
    <>
      <header style={headerStyle}>
        <NavLink to="/">
          <img src={logo} alt="Hookah Forge" style={logoStyle} />
        </NavLink>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
          }}
          ref={dropdownRef}
        >
          {userProfile.isLoggedIn ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '8px',
                }}
                onClick={() => setShowDropdown(!showDropdown)}
              >
                {userProfile.avatarUrl ? (
                  <img
                    src={userProfile.avatarUrl}
                    alt="avatar"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '1px solid rgba(231, 228, 223, 0.2)',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(231, 228, 223, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(231, 228, 223, 0.2)',
                    }}
                  >
                    <User size={18} color="var(--color-text)" />
                  </div>
                )}
                <ChevronDown size={16} color="var(--color-text)" opacity={0.6} />
              </div>

              {showDropdown && (
                <div style={dropdownMenuStyle}>
                  <div style={{ padding: '8px 20px', borderBottom: '1px solid rgba(231, 228, 223, 0.1)', marginBottom: '8px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{userProfile.username}</div>
                    <div style={{ fontSize: '12px', opacity: 0.6 }}>{userProfile.email}</div>
                  </div>
                  <NavLink to="/" style={dropdownItemStyle} onClick={() => setShowDropdown(false)}>
                    <LayoutDashboard size={16} /> ダッシュボード
                  </NavLink>
                  <NavLink to="/favorites" style={dropdownItemStyle} onClick={() => setShowDropdown(false)}>
                    <Heart size={16} /> お気に入り
                  </NavLink>
                  <NavLink to="/settings" style={dropdownItemStyle} onClick={() => setShowDropdown(false)}>
                    <Settings size={16} /> 設定
                  </NavLink>
                  <button
                    type="button"
                    style={{ ...dropdownItemStyle, color: 'var(--color-error)' }}
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> ログアウト
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <User
                size={24}
                color="var(--color-text)"
                style={{ marginRight: '10px' }}
              />
              <button
                style={buttonStyle}
                onClick={() => openModal('login')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor =
                    'rgba(231, 228, 223, 0.1)';
                }}
              >
                ログイン
              </button>
              <button
                style={primaryButtonStyle}
                onClick={() => openModal('register')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor =
                    'rgba(231, 228, 223, 0.1)';
                }}
              >
                新規登録
              </button>
            </>
          )}
        </div>
      </header>

      {showModal && (
        <div
          style={modalOverlayStyle}
          onClick={() => {
            setShowModal(false);
            setErrorMessage('');
          }}
        >
          <form
            className="glass-panel"
            style={{
              ...modalContentStyle,
              backgroundColor: 'var(--color-surface)',
              border: '1px solid rgba(231, 228, 223, 0.1)',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
            onSubmit={handleAuthSubmit}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                fontSize: 'var(--font-size-h2)',
                margin: 0,
                fontFamily: 'var(--font-heading-ja)',
                color: 'var(--color-text)',
              }}
            >
              {modalType === 'login' ? 'ログイン' : '新規登録'}
            </h2>

            {errorMessage && (
              <div
                style={{
                  color: 'var(--color-error)',
                  fontSize: '13px',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {errorMessage}
              </div>
            )}

            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <label style={{ fontSize: '13px', opacity: 0.8 }}>
                メールアドレス
              </label>
              <input
                type="email"
                required
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="email@example.com"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  backgroundColor: 'rgba(26, 28, 31, 0.6)',
                  color: '#fff',
                  outline: 'none',
                }}
              />
            </div>

            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <label style={{ fontSize: '13px', opacity: 0.8 }}>
                パスワード
                {modalType === 'register' && (
                  <span
                    style={{
                      fontSize: '11px',
                      opacity: 0.6,
                      display: 'block',
                      marginTop: '4px',
                    }}
                  >
                    （英大文字・小文字各1文字以上含む、6文字以上）
                  </span>
                )}
              </label>
              <input
                type="password"
                required
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="••••••••"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  backgroundColor: 'rgba(26, 28, 31, 0.6)',
                  color: '#fff',
                  outline: 'none',
                }}
              />
            </div>

            <button
              type="submit"
              style={{
                ...primaryButtonStyle,
                width: '100%',
                padding: '12px',
                margin: 0,
                marginTop: '10px',
                cursor: 'pointer',
                backgroundColor: 'var(--color-primary)',
                border: 'none',
                color: '#fff',
                fontWeight: 'bold',
              }}
            >
              {modalType === 'login' ? 'ログインする' : '登録する'}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default Header;
